import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { State } from '../store';

interface TeamModeContextValue {
  isTeamMode: boolean;
  setIsTeamMode: (value: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const TeamModeContext = createContext<TeamModeContextValue>({
  isTeamMode: false,
  setIsTeamMode: noop,
});

const TEAM_ROUTE_PREFIXES = [
  '/dashboard/team',
  '/dashboard/emissions',
  '/settings/team',
];

// Routes that should preserve the current isTeamMode state rather than
// resetting it. For example, /checkout is reached from both individual
// (/dashboard) and team (/dashboard/team) flows — forcing false here
// would fight the checkout attribution toggle.
const NEUTRAL_ROUTE_PREFIXES = ['/checkout'];

export function TeamModeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const user = useSelector((state: State) => state.user);
  const hasTeam = !!user.team;

  const isTeamRoute = TEAM_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  const isNeutralRoute = NEUTRAL_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  const [isTeamMode, setIsTeamMode] = useState(hasTeam && isTeamRoute);

  useEffect(() => {
    if (!hasTeam) {
      setIsTeamMode(false);
    } else if (isTeamRoute) {
      setIsTeamMode(true);
    } else if (!isNeutralRoute) {
      setIsTeamMode(false);
    }
    // On neutral routes (e.g. /checkout), leave isTeamMode as-is so the
    // value carried over from the previous route is preserved.
  }, [hasTeam, isTeamRoute, isNeutralRoute]);

  return (
    <TeamModeContext.Provider value={{ isTeamMode, setIsTeamMode }}>
      {children}
    </TeamModeContext.Provider>
  );
}

export default function useTeamMode() {
  return useContext(TeamModeContext);
}
